import { useGT } from 'gt-react';
import type { AgentRunCategory, AgentRunSummary } from '@erp/rpc';
import { runDidNotStart, runWasSkipped } from './agentPageRuns';

/** The categories under each heading of the History filter, in display order. */
export const RUN_CATEGORY_GROUPS = [
  { key: 'running', categories: ['running'] },
  { key: 'succeeded', categories: ['succeeded'] },
  { key: 'didNotStart', categories: ['skipped', 'refused', 'couldnt_start'] },
  {
    key: 'failed',
    categories: ['cancelled', 'stalled', 'interrupted', 'blocked', 'failed'],
  },
] as const satisfies readonly {
  key: string;
  categories: readonly AgentRunCategory[];
}[];

export type RunCategoryGroupKey = (typeof RUN_CATEGORY_GROUPS)[number]['key'];

/**
 * The exact rule behind each category, as the fields it reads, with the
 * earlier rules a run must miss spelled out (the backend checks them in
 * order, first match wins). Field names and stored values, so not
 * translated; mirrors the backend's rule table (`agentRunCategory.ts`), the
 * source of truth.
 */
export const RUN_CATEGORY_RULES: Record<AgentRunCategory, readonly string[]> = {
  running: ['status = running'],
  succeeded: ['status = completed', 'finishReason ≠ not_started'],
  skipped: [
    'status = completed',
    'finishReason = not_started',
    'triggerKind ≠ manual',
  ],
  refused: [
    'status = completed',
    'finishReason = not_started',
    'triggerKind = manual',
  ],
  couldnt_start: [
    'status = failed',
    'finishReason = not_started | launch_failed',
  ],
  cancelled: [
    'status = failed',
    'error = "Cancelled by user"',
    'finishReason ≠ not_started | launch_failed',
  ],
  stalled: [
    'status = failed',
    'finishReason = no_progress',
    'error ≠ "Cancelled by user"',
  ],
  interrupted: [
    'status = failed',
    'finishReason = interrupted',
    'error ≠ "Cancelled by user"',
  ],
  blocked: ['status = blocked'],
  failed: [
    'status = failed',
    'finishReason ≠ not_started | launch_failed | no_progress | interrupted',
    'error ≠ "Cancelled by user"',
  ],
};

/** Categories of runs that never did any work. */
const DID_NOT_START: ReadonlySet<AgentRunCategory> = new Set([
  'skipped',
  'refused',
  'couldnt_start',
]);

/**
 * Whether a run never did any work. Reads the server's category; falls back
 * to `finishReason` for a backend that predates it.
 */
export function runNeverStarted(
  run: Pick<AgentRunSummary, 'category' | 'finishReason'>,
): boolean {
  return run.category ? DID_NOT_START.has(run.category) : runDidNotStart(run);
}

/**
 * Whether a run is a skipped check. Reads the server's category; falls back
 * to the AI-529 rule for a backend that predates it.
 */
export function runIsSkipped(
  run: Pick<
    AgentRunSummary,
    'category' | 'finishReason' | 'status' | 'triggerKind'
  >,
): boolean {
  return run.category ? run.category === 'skipped' : runWasSkipped(run);
}

export interface RunCategoryCopy {
  label: string;
  /** What the category means, in plain words. */
  description: string;
}

/** The label and explanation for each category and heading, translated. */
export function useRunCategoryCopy(): {
  categories: Record<AgentRunCategory, RunCategoryCopy>;
  groups: Record<RunCategoryGroupKey, string>;
} {
  const gt = useGT();
  const label = 'agent page history run-type filter: name of a kind of run';
  const description =
    'agent page history run-type filter: what a kind of run means';
  const group = 'agent page history run-type filter: heading over kinds of run';
  return {
    groups: {
      running: gt('Running', { context: group }),
      succeeded: gt('Succeeded', { context: group }),
      didNotStart: gt("Didn't start", { context: group }),
      failed: gt('Failed', { context: group }),
    },
    categories: {
      running: {
        label: gt('Running', { context: label }),
        description: gt('Still going.', { context: description }),
      },
      succeeded: {
        label: gt('Succeeded', { context: label }),
        description: gt('Finished and did its work.', { context: description }),
      },
      skipped: {
        label: gt('Skipped', { context: label }),
        description: gt(
          "The agent was only waiting: its start conditions weren't met, or a resource it needs was busy.",
          { context: description },
        ),
      },
      refused: {
        label: gt('Refused', { context: label }),
        description: gt(
          "Someone pressed Run, but the agent couldn't start yet: its start conditions weren't met, or a resource it needs was busy.",
          { context: description },
        ),
      },
      couldnt_start: {
        label: gt("Couldn't start", { context: label }),
        description: gt(
          'Failed before doing any work, for example a paused resource or a launch that failed. Someone needs to look.',
          { context: description },
        ),
      },
      cancelled: {
        label: gt('Cancelled', { context: label }),
        description: gt('Someone pressed Stop.', { context: description }),
      },
      stalled: {
        label: gt('Stalled', { context: label }),
        description: gt(
          'Kept going without making progress, so it was stopped.',
          { context: description },
        ),
      },
      interrupted: {
        label: gt('Interrupted', { context: label }),
        description: gt(
          'Cut off partway, for example by a deploy or a restart. A retry usually picks it back up.',
          { context: description },
        ),
      },
      blocked: {
        label: gt('Blocked', { context: label }),
        description: gt(
          'Interrupted, then the agent was published again, so it could not safely pick up where it left off.',
          { context: description },
        ),
      },
      failed: {
        label: gt('Other failures', { context: label }),
        description: gt(
          'Failed for any other reason. The run shows the error.',
          { context: description },
        ),
      },
    },
  };
}
